import type { ErrorComponentProps } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';

/**
 * Shown while a route's `beforeLoad`/loader is pending past the router's
 * `defaultPendingMs`. Keeps a cold-cache landing (session + `me` fetch) from
 * showing a blank screen for ~1s+.
 */
export function RoutePending() {
  return (
    <div className="grid min-h-screen place-items-center" role="status" aria-live="polite">
      <div className="size-6 animate-spin rounded-full border-2 border-muted border-t-foreground" />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

/**
 * Router error boundary. Catches errors thrown out of `beforeLoad`/loaders —
 * notably a transient `/api/users/me` or session-endpoint failure that the
 * auth guards deliberately rethrow (rather than dead-ending the user on
 * /no-access). Offers a retry instead of a blank screen.
 */
export function RouteError(_props: ErrorComponentProps) {
  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div className="w-full max-w-md space-y-4 rounded-lg border border-border bg-background p-8 text-center shadow-sm">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="text-sm text-muted-foreground">
          Clockwise couldn't finish loading. This is usually temporary.
        </p>
        <Button className="w-full" onClick={() => window.location.reload()}>
          Try again
        </Button>
      </div>
    </div>
  );
}
